if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        
        // Remove any teleported quickadd size guide modals from body
        document.querySelectorAll('.size-guide-modal[id*="quickadd"]').forEach((modal) => {
          modal.remove();
          document.body.style.overflow = '';
        });

        this.classList.remove('active');
        this.classList.add('closing');
        setTimeout(() => {
          this.modalContent.innerHTML = '';
          if (preventFocus) this.openedBy = null;
          super.hide();
          this.classList.remove('closing');
        }, 600);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Clean up any old quickadd size guide modals before showing new quick add
        document.querySelectorAll('.size-guide-modal[id*="quickadd"]').forEach((modal) => {
          modal.remove();
          document.body.style.overflow = '';
        });

        const handleResponse = (responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector('product-info');

          this.preprocessHTML(productElement);
          this.clearDefaultSizeSelection(productElement);
          HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

          // Track product in recently viewed when Quick View is opened
          const productId = productElement.getAttribute('data-product-id');
          if (productId && typeof window.addRecentlyViewedProduct === 'function') {
            window.addRecentlyViewedProduct(productId);
          }

          // Force layout reflow to register initial styles (hidden opacity and transform) before opening
          this.offsetHeight;
          this.modalContent.offsetHeight;

          super.show(opener);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.classList.add('active');
            });
          });
        };

        const url = opener.getAttribute('data-product-url');
        const cachedPromise = QuickAddModal._prefetchCache?.get(url);
        const executeFetch = () => fetch(url).then((r) => r.text());

        // If cache exists, catch any errors and fallback to fresh fetch, otherwise execute fetch
        (cachedPromise ? cachedPromise.catch(executeFetch) : executeFetch())
          .then(handleResponse)
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }


      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      clearDefaultSizeSelection(productElement) {
        // Uncheck all radio inputs or reset select elements matching "size"
        const sizeContainers = productElement.querySelectorAll('fieldset[data-option-name*="size" i], [data-option-name*="size" i]');
        sizeContainers.forEach((container) => {
          const radios = container.querySelectorAll('input[type="radio"]');
          radios.forEach((radio) => {
            radio.checked = false;
            radio.removeAttribute('checked');
            radio.classList.remove('active');
          });

          const select = container.tagName === 'SELECT' ? container : container.querySelector('select');
          if (select) {
            select.value = '';
          }

          const selectedValueSpan = container.querySelector('[data-selected-value]');
          if (selectedValueSpan) {
            selectedValueSpan.textContent = '';
          }
        });

        // Also clean up any checked custom swatch labels/inputs
        const allSizeInputs = Array.from(productElement.querySelectorAll('input, select')).filter(el => {
          const opt = (el.getAttribute('data-option-name') || el.closest('fieldset')?.getAttribute('data-option-name') || '').toLowerCase();
          const nm = (el.getAttribute('name') || '').toLowerCase();
          return opt.includes('size') || nm.includes('size');
        });
        allSizeInputs.forEach(el => {
          if (el.tagName === 'SELECT') {
            el.value = '';
          } else if (el.type === 'radio' || el.type === 'checkbox') {
            el.checked = false;
            el.removeAttribute('checked');
          }
        });

        // Clear the variant ID input value — product-form.js checks !variantInput.value to block submission
        const variantInput = productElement.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = '';
          variantInput.removeAttribute('value');
        }
      }





      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Completely sever connection with Size Guide in Quick View
        // const sizeGuideModals = productElement.querySelectorAll('.size-guide-modal, [id*="SizeGuide"]');
        // if (sizeGuideModals) sizeGuideModals.forEach((modal) => modal.remove());

        // const sizeGuideLinks = productElement.querySelectorAll('.size-guide-link');
        // if (sizeGuideLinks) sizeGuideLinks.forEach((link) => link.remove());

        // const scripts = productElement.querySelectorAll('script');
        // scripts.forEach((script) => {
        //   if (script.textContent.includes('SizeGuide') || script.textContent.includes('initSizeGuide')) {
        //     script.remove();
        //   }
        // });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const randomSuffix = Math.random().toString(36).substring(2, 10);

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}-${randomSuffix}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }


      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

// ─── Hover Prefetch ───────────────────────────────────────────────────────────
// When user hovers over a quick-add opener, start fetching + caching product HTML.
// This gives ~200–400ms head start before they actually click.
// Cache is a Map of url → Promise<string> so repeated hovers don't re-fetch.
{
  if (!QuickAddModal._prefetchCache) {
    QuickAddModal._prefetchCache = new Map();
  }

  // Use event delegation on document — works for dynamically added cards too
  document.addEventListener('mouseover', (e) => {
    const opener = e.target.closest('modal-opener[data-product-url]');
    if (!opener) return;
    const url = opener.getAttribute('data-product-url');
    if (!url || QuickAddModal._prefetchCache.has(url)) return;

    // Fetch and cache as a Promise<string> — show() will .then() it directly
    const promise = fetch(url, { priority: 'low' })
      .then((r) => r.ok ? r.text() : Promise.reject())
      .catch(() => {
        // Remove failed cache entry so next click retries
        QuickAddModal._prefetchCache.delete(url);
        return Promise.reject();
      });

    QuickAddModal._prefetchCache.set(url, promise);
  }, { passive: true });
}

// ─── Instant Mobile Size Selection Sheet ──────────────────────────────────────
// Decodes and parses pre-rendered JSON payload inside the product card on mobile
// grid views and renders the mobile size selection sheet directly (0ms latency).
window.openInstantMobileSizeSheet = function(openerElement, button) {
  const card = openerElement.closest('.card-wrapper') || openerElement.closest('.product-card-wrapper') || openerElement.closest('product-component');
  if (!card) return false;
  
  const jsonContainer = card.querySelector('[data-in-json]');
  if (!jsonContainer) return false;
  
  try {
    const rawContent = jsonContainer.textContent.trim();
    if (!rawContent) return false;
    
    let decodedData;
    if (rawContent.startsWith('{')) {
      decodedData = JSON.parse(rawContent);
    } else {
      const binaryString = atob(rawContent);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const decodedString = new TextDecoder('utf-8').decode(bytes);
      decodedData = JSON.parse(decodedString);
    }
    const productData = decodedData.productData;
    if (!productData || !productData.variants || productData.variants.length === 0) return false;
    
    // Find option index for "size"
    const sizeOptionIndex = productData.options.findIndex(opt => opt.toLowerCase().includes('size'));
    if (sizeOptionIndex === -1) return false; // Fallback to standard Quick Add if no size option exists
    
    // Gather unique sizes and map to variants
    const sizes = [];
    const seenSizes = new Set();
    
    productData.variants.forEach(variant => {
      const sizeValue = variant.options[sizeOptionIndex];
      if (sizeValue && !seenSizes.has(sizeValue)) {
        seenSizes.add(sizeValue);
        sizes.push({
          id: variant.id,
          value: sizeValue,
          available: variant.available,
          price: variant.price,
          compare_at_price: variant.compare_at_price
        });
      }
    });
    
    if (sizes.length === 0) return false;
    
    // Create or locate the mobile size sheet overlay
    let overlay = document.querySelector('.mobile-size-sheet-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'mobile-size-sheet-overlay';
      document.body.appendChild(overlay);
    }

    // Clean up any old container instance and recreate to avoid duplicate/stale event listeners
    const oldContainer = overlay.querySelector('.mobile-size-sheet-container');
    if (oldContainer) {
      oldContainer.remove();
    }

    const container = document.createElement('div');
    container.className = 'mobile-size-sheet-container';
    container.innerHTML = `
      <button type="button" class="mobile-size-sheet-close-btn" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      
      <!-- Header with Image & Title & Price -->
      <div class="mobile-size-sheet-header">
        <div class="mobile-size-sheet-header-image">
          <img src="${productData.featured_image || ''}" alt="${productData.title || ''}" width="80" height="100">
        </div>
        <div class="mobile-size-sheet-header-details">
          <div class="mobile-size-sheet-header-title">${productData.title || ''}</div>
          <div class="mobile-size-sheet-header-price-container">
            <div class="mobile-size-sheet-price"></div>
            <div class="mobile-size-sheet-tax">MRP inclusive of all Taxes</div>
          </div>
        </div>
      </div>
      
      <div class="mobile-size-sheet-title-wrapper">
        <div class="mobile-size-sheet-title">APPAREL SIZE</div>
        ${productData.size_guide && productData.size_guide.content ? `
          <button type="button" class="mobile-size-sheet-size-guide-btn">Size Guide</button>
        ` : ''}
      </div>
      
      <div class="mobile-size-sheet-error" style="display: none;">Please select your size*</div>
      <div class="mobile-size-sheet-options"></div>
      <div class="mobile-size-sheet-divider"></div>
      <div class="mobile-size-sheet-footer">
        <button type="button" class="mobile-size-sheet-submit-btn">ADD TO CART</button>
      </div>
    `;
    overlay.appendChild(container);

    // Attach Close events to overlay
    container.querySelector('.mobile-size-sheet-close-btn').addEventListener('click', () => {
      overlay.classList.remove('active');
    });

    if (!overlay.dataset.listenerAttached) {
      overlay.dataset.listenerAttached = 'true';
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.classList.remove('active');
        }
      });
    }

    // Attach Size Guide button click listener
    const sizeGuideBtn = container.querySelector('.mobile-size-sheet-size-guide-btn');
    if (sizeGuideBtn && productData.size_guide && productData.size_guide.content) {
      sizeGuideBtn.addEventListener('click', () => {
        let mobileSizeGuideModal = document.querySelector('#SizeGuideModal-quickadd-mobile');
        if (!mobileSizeGuideModal) {
          mobileSizeGuideModal = document.createElement('div');
          mobileSizeGuideModal.id = 'SizeGuideModal-quickadd-mobile';
          mobileSizeGuideModal.className = 'size-guide-modal';
          mobileSizeGuideModal.setAttribute('role', 'dialog');
          mobileSizeGuideModal.setAttribute('aria-modal', 'true');
          document.body.appendChild(mobileSizeGuideModal);
        }

        mobileSizeGuideModal.innerHTML = `
          <div class="size-guide-modal__content">
            <button id="SizeGuideClose-quickadd-mobile" type="button" class="size-guide-modal__close" aria-label="Close modal">
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-close" style="width: 1.8rem; height: 1.8rem;">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </span>
            </button>
            <div class="size-guide-modal__body">
              <h2 id="SizeGuideHeading-quickadd-mobile" class="h1">${productData.size_guide.title}</h2>
              <div class="rte">
                ${productData.size_guide.content}
              </div>
            </div>
          </div>
        `;

        mobileSizeGuideModal.querySelector('.size-guide-modal__close').addEventListener('click', () => {
          mobileSizeGuideModal.classList.remove('active');
        });

        mobileSizeGuideModal.addEventListener('click', (e) => {
          if (e.target === mobileSizeGuideModal) {
            mobileSizeGuideModal.classList.remove('active');
          }
        });

        if (typeof window.initializeSizeGuideToggles === 'function') {
          window.initializeSizeGuideToggles(mobileSizeGuideModal);
        }
        mobileSizeGuideModal.classList.add('active');
      });
    }
    
    // Track selected state on the overlay
    overlay.selectedVariantId = null;
    overlay.productData = productData;
    
    // Set matching option name in title (e.g., APPAREL SIZE, size, shoe size, etc.)
    const titleOptionName = productData.options[sizeOptionIndex].toUpperCase();
    container.querySelector('.mobile-size-sheet-title').textContent = titleOptionName;
    
    // Populate size buttons
    const optionsWrapper = container.querySelector('.mobile-size-sheet-options');
    optionsWrapper.innerHTML = '';
    
    sizes.forEach(sz => {
      const optBtn = document.createElement('div');
      optBtn.className = `mobile-size-sheet-option${!sz.available ? ' disabled' : ''}`;
      optBtn.textContent = sz.value;
      optBtn.dataset.variantId = sz.id;
      
      if (sz.available) {
        optBtn.addEventListener('click', () => {
          optionsWrapper.querySelectorAll('.mobile-size-sheet-option').forEach(btn => btn.classList.remove('active'));
          optBtn.classList.add('active');
          overlay.selectedVariantId = sz.id;
          container.querySelector('.mobile-size-sheet-error').style.display = 'none';
          
          updateInstantPriceDisplay(container, sz.price, sz.compare_at_price, card);
        });
      }
      optionsWrapper.appendChild(optBtn);
    });
    
    // Initialize pricing with first available variant
    const initialVariant = productData.variants.find(v => v.available) || productData.variants[0];
    updateInstantPriceDisplay(container, initialVariant.price, initialVariant.compare_at_price, card);
    
    // Ensure error message is hidden initially
    container.querySelector('.mobile-size-sheet-error').style.display = 'none';
    
    // Attach submit event listener to CTA button
    const submitBtn = container.querySelector('.mobile-size-sheet-submit-btn');
    const newSubmitBtn = submitBtn.cloneNode(true);
    submitBtn.parentNode.replaceChild(newSubmitBtn, submitBtn);
    
    newSubmitBtn.addEventListener('click', () => {
      if (!overlay.selectedVariantId) {
        container.querySelector('.mobile-size-sheet-error').style.display = 'block';
        return;
      }
      
      // Disable CTA and show loader spinner inside it instantly (0ms)
      newSubmitBtn.disabled = true;
      newSubmitBtn.style.pointerEvents = 'none';
      newSubmitBtn.classList.add('loading');
      
      const originalBtnText = newSubmitBtn.textContent;
      newSubmitBtn.innerHTML = `
        <div class="loading__spinner" style="width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center;">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" style="width: 20px; height: 20px;">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="currentColor"></circle>
          </svg>
        </div>
      `;
      
      addVariantToCartDirectly(overlay.selectedVariantId, button, newSubmitBtn, originalBtnText, overlay);
    });
    
    // Slide up bottom sheet overlay
    setTimeout(() => {
      overlay.classList.add('active');
      // Instantly clear loader spinner on the card button once sheet is successfully opened
      if (button) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
        const btnText = button.querySelector('.quick-add__icon') || button.querySelector('span');
        if (btnText) btnText.style.display = '';
      }
    }, 10);
    
    return true;
  } catch (err) {
    console.error('Error opening instant size sheet from JSON:', err);
    return false;
  }
};

function updateInstantPriceDisplay(overlay, price, comparePrice, card) {
  const priceDisplay = overlay.querySelector('.mobile-size-sheet-price');
  if (!priceDisplay) return;
  
  const formatMoney = (cents, cardElement) => {
    const cardPriceEl = cardElement ? (cardElement.querySelector('.price-item--sale') || cardElement.querySelector('.price')) : null;
    let symbol = '₹';
    if (cardPriceEl) {
      const text = cardPriceEl.textContent.trim();
      const match = text.match(/^([^\d\s,.]+)/) || text.match(/([^\d\s,.]+)$/);
      if (match) symbol = match[1];
    }
    let val = typeof cents === 'number' ? cents : parseFloat(cents || 0);
    if (val > 1000) val = val / 100;
    const amountVal = val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol}${amountVal}`;
  };
  
  let priceHtml = `<span class="price-item price-item--sale">${formatMoney(price, card)}</span>`;
  if (comparePrice && comparePrice > price) {
    priceHtml += ` <s>${formatMoney(comparePrice, card)}</s>`;
  }
  priceDisplay.innerHTML = priceHtml;
}

function addVariantToCartDirectly(variantId, button, sheetSubmitBtn, originalBtnText, overlay) {
  // Show visual loading state on card's button as well
  if (button) {
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    const btnText = button.querySelector('.quick-add__icon') || button.querySelector('span');
    if (btnText) btnText.style.display = 'none';
  }
  
  const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const sections = cartNotification ? cartNotification.getSectionsToRender().map((s) => s.id) : [];
  const productUrl = button ? button.getAttribute('data-product-url') : '';

  // Extract product handle to populate ShopifyAnalytics and meta objects.
  // This prevents shop_events_listener.js from requesting /products/null.json 404s (5x requests), saving network connections and CPU.
  if (productUrl && productUrl.includes('/products/')) {
    const urlParts = productUrl.split('/');
    const productHandle = urlParts[urlParts.length - 1].split('?')[0];
    if (productHandle) {
      window.ShopifyAnalytics = window.ShopifyAnalytics || {};
      window.ShopifyAnalytics.meta = window.ShopifyAnalytics.meta || {};
      window.ShopifyAnalytics.meta.product = window.ShopifyAnalytics.meta.product || {};
      window.ShopifyAnalytics.meta.product.handle = productHandle;

      window.meta = window.meta || {};
      window.meta.product = window.meta.product || {};
      window.meta.product.handle = productHandle;
    }
  }
  
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);
  if (sections.length > 0) {
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.routes?.cart_url || '/cart');
  }
  
  const baseAddUrl = window.routes?.cart_add_url || '/cart/add';
  const fetchUrl = baseAddUrl.endsWith('.js') ? baseAddUrl : `${baseAddUrl}.js`;

  fetch(fetchUrl, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: formData
  })
  .then(response => response.json())
  .then(response => {
    if (response.status) {
      alert(response.description || response.message || 'Could not add variant to cart.');
      return;
    }
    
    // Close the overlay ONLY after the request succeeds
    if (overlay) {
      overlay.classList.remove('active');
    }
    
    // Fire cartUpdate PUB_SUB event so cart drawer updates automatically
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: variantId,
        cartData: response,
      });
    }
    
    if (cartNotification) {
      if (cartNotification.classList.contains('is-empty')) {
        cartNotification.classList.remove('is-empty');
      }
      cartNotification.renderContents(response);
    }
  })
  .catch(err => {
    console.error('Error adding variant to cart via AJAX:', err);
    // If it failed, restore the sheet submit button state so the user can try again
    if (sheetSubmitBtn) {
      sheetSubmitBtn.disabled = false;
      sheetSubmitBtn.style.pointerEvents = '';
      sheetSubmitBtn.classList.remove('loading');
      sheetSubmitBtn.textContent = originalBtnText;
    }
    if (overlay) {
      const errorMsg = overlay.querySelector('.mobile-size-sheet-error');
      if (errorMsg) {
        errorMsg.textContent = 'Failed to add item. Please try again.';
        errorMsg.style.display = 'block';
      }
    }
  })
  .finally(() => {
    // Reset visual state of card button
    if (button) {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      const btnText = button.querySelector('.quick-add__icon') || button.querySelector('span');
      if (btnText) btnText.style.display = '';
    }
  });
}

window.initializeSizeGuideToggles = function(modal) {
  if (modal.dataset.togglesInitialized) return;
  modal.dataset.togglesInitialized = 'true';
  const rte = modal.querySelector('.rte');
  if (!rte) return;
  const tables = rte.querySelectorAll('table');
  if (tables.length >= 2) {
    const elements = Array.from(rte.querySelectorAll('*'));
    let inBtn = null;
    let cmBtn = null;
    
    for (const el of elements) {
      const text = el.textContent.trim().toUpperCase();
      if (text === 'IN.' || text === 'IN' || text === 'INCH') {
        if (el.children.length === 0) inBtn = el;
      } else if (text === 'CM.' || text === 'CM' || text === 'CMS') {
        if (el.children.length === 0) cmBtn = el;
      }
    }
    
    if (inBtn && cmBtn) {
      const styleButton = (btn) => {
        btn.style.cursor = 'pointer';
        btn.style.display = 'inline-block';
        btn.style.padding = '6px 14px';
        btn.style.border = '1px solid #d3d3d3';
        btn.style.borderRadius = '4px';
        btn.style.marginRight = '8px';
        btn.style.fontSize = '12px';
        btn.style.fontWeight = '500';
        btn.style.textAlign = 'center';
        btn.style.userSelect = 'none';
        btn.style.textTransform = 'uppercase';
        btn.style.transition = 'all 0.2s ease';
      };
      
      styleButton(inBtn);
      styleButton(cmBtn);
      
      const setActiveTab = (unit) => {
        if (unit === 'cm') {
          tables[0].style.display = 'table';
          tables[1].style.display = 'none';
          
          cmBtn.style.backgroundColor = '#223c8c';
          cmBtn.style.color = '#ffffff';
          cmBtn.style.borderColor = '#223c8c';
          
          inBtn.style.backgroundColor = '#ffffff';
          inBtn.style.color = '#121212';
          inBtn.style.borderColor = '#d3d3d3';
        } else {
          tables[0].style.display = 'none';
          tables[1].style.display = 'table';
          
          inBtn.style.backgroundColor = '#223c8c';
          inBtn.style.color = '#ffffff';
          inBtn.style.borderColor = '#223c8c';
          
          cmBtn.style.backgroundColor = '#ffffff';
          cmBtn.style.color = '#121212';
          cmBtn.style.borderColor = '#d3d3d3';
        }
      };
      
      inBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveTab('in');
      });
      
      cmBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveTab('cm');
      });
      
      setActiveTab('cm');
    }
  }
}

